import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import Field from "../components/Field";
import { useAuth } from "../context/AuthContext";
import {
  validateEmail,
  validatePassword,
  validateName,
  validateBirthDate,
  validateGender,
} from "../api/validators";
import OAuthDivider from "../components/OAuthDivider";
import { CAPABILITIES } from "../api/authGateway";
import { Button } from "../components/ui";
import usePageTitle from "../hooks/usePageTitle";
import logo from "../assets/logo.png";
import YandexLoginButton from "../components/YandexLoginButton";
import TripleCaptcha from "../components/TripleCaptcha";
import "../components/AuthForm.css";

/**
 * Регистрация. Аккаунт для ребёнка создаёт родитель.
 * Собираем сразу всё: имя, email, пароль, дату рождения, пол —
 * чтобы не доспрашивать в приложении. signup → login →
 * grant-default-skin (дефолтный скин по полу) → редирект.
 *
 * Поля «повторите пароль» нет намеренно — у Roblox его тоже нет, форма
 * и так длинная (есть согласия 152-ФЗ). Вместо него — «показать пароль».
 */
export default function SignupPage() {
  usePageTitle("Регистрация");
  const { signup, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [gender, setGender] = useState(""); // "male" | "female"
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Ref для скролла к блоку с ошибкой — пользователь ждёт у кнопки внизу,
  // а ошибка из бэка («Недопустимое имя», «email занят») рисуется наверху.
  // Без скролла юзер видит только loading и не понимает что произошло.
  const formErrorRef = useRef(null);

  // При появлении ошибки — плавно скроллим к ней и подсвечиваем.
  useEffect(() => {
    if (formError && formErrorRef.current) {
      formErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [formError]);

  // Тройная капча: обязательна для всех регистраций.
  // Реализация в TripleCaptcha — отдаёт collect()/refresh() через ref.
  const captchaRef = useRef(null);

  // Два раздельных согласия (152-ФЗ ст. 9 — согласие на ПДн ребёнка
  // должно быть явным и отдельным).
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePdn, setAgreePdn] = useState(false);

  // Границы для <input type="date"> — нельзя выбрать дату в будущем
  // (max = сегодня) и старше 120 лет (min). Совпадает с validateBirthDate.
  const today = new Date();
  const maxBirthDate = today.toISOString().slice(0, 10);
  const minBirthDate = new Date(
    today.getFullYear() - 120,
    today.getMonth(),
    today.getDate()
  )
    .toISOString()
    .slice(0, 10);

  useEffect(() => {
    if (isAuthenticated) navigate("/app", { replace: true });
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!agreeTerms) {
      setFormError(
        "Подтвердите, что вы законный представитель и принимаете Пользовательское соглашение"
      );
      return;
    }
    if (!agreePdn) {
      setFormError(
        "Для регистрации необходимо согласие на обработку персональных данных"
      );
      return;
    }

    const fieldErrors = {
      firstName: validateName(firstName),
      email: validateEmail(email),
      password: validatePassword(password),
      birthDate: validateBirthDate(birthDate),
      gender: validateGender(gender),
    };
    setErrors(fieldErrors);
    if (Object.values(fieldErrors).some(Boolean)) return;

    /* 🔴 КАПЧУ ПРОВЕРЯЕМ, ТОЛЬКО ЕСЛИ ОНА ПОКАЗАНА. Виджет рисуется по флагу
       CAPABILITIES.captcha (в identity v2 капчи нет), а проверка стояла
       безусловно: картинок на экране не было, а форма требовала «коды с
       обеих картинок» — зарегистрироваться было невозможно (25.09.2026). */
    let cap = null;
    if (CAPABILITIES.captcha) {
      cap = captchaRef.current ? captchaRef.current.collect() : null;
      if (!cap || !cap.captchaAnswerLeft || !cap.captchaAnswerRight) {
        setFormError("Введите коды с обеих картинок.");
        return;
      }
      if (!cap.canvasData || cap.canvasData.length < 200) {
        setFormError("Нарисуйте кота в центральном поле.");
        return;
      }
    }

    setSubmitting(true);
    try {
      await signup({
        email: email.trim(),
        password,
        firstName: firstName.trim(),
        birthDate,
        gender,
        ...(cap ? {
          captchaIdLeft: cap.captchaIdLeft,
          captchaAnswerLeft: cap.captchaAnswerLeft,
          captchaIdRight: cap.captchaIdRight,
          captchaAnswerRight: cap.captchaAnswerRight,
          canvasData: cap.canvasData,
        } : {}),
      });
      navigate("/app", { replace: true });
    } catch (err) {
      const msg = err.message || "";
      // При любой ошибке капчи (или другой) — перегенерим пару, чтобы старые
      // challenge-ID не залипали (бэк удаляет их через TTL=5 мин в любом случае).
      if (captchaRef.current) {
        try { await captchaRef.current.refresh(); } catch { /* noop */ }
      }
      setFormError(msg || "Не удалось зарегистрироваться. Попробуйте ещё раз.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="rb-auth">
        <form
          className="rb-auth__card rb-auth__card--wide"
          onSubmit={handleSubmit}
          noValidate
        >
          <img src={logo} alt="Рублокс" className="rb-auth__logo" />
          <h1 className="rb-auth__title">Регистрация в Рублокс</h1>
          <p className="rb-auth__subtitle">
            Аккаунт для ребёнка создаёт родитель. Один аккаунт — доступ к
            платформе и онлайн-школе «Майнкрафтия».
          </p>

          {formError && (
            <div ref={formErrorRef} className="rb-auth__error">{formError}</div>
          )}

          {/* имя + дата рождения в один ряд */}
          <div className="rb-auth__row">
            <Field
              id="signup-name"
              label="Имя ребёнка"
              value={firstName}
              onChange={setFirstName}
              placeholder="Как зовут"
              error={errors.firstName}
              autoComplete="off"
              disabled={submitting}
            />
            <Field
              id="signup-bdate"
              label="Дата рождения"
              type="date"
              value={birthDate}
              onChange={setBirthDate}
              error={errors.birthDate}
              disabled={submitting}
              min={minBirthDate}
              max={maxBirthDate}
            />
          </div>

          {/* пол — компактный сегментированный переключатель */}
          <div className="rb-field">
            <label className="rb-field__label">Пол</label>
            <div className="rb-gender">
              <button
                type="button"
                className={`rb-gender__opt ${
                  gender === "male" ? "is-active" : ""
                }`}
                onClick={() => setGender("male")}
                disabled={submitting}
              >
                Мальчик
              </button>
              <button
                type="button"
                className={`rb-gender__opt ${
                  gender === "female" ? "is-active" : ""
                }`}
                onClick={() => setGender("female")}
                disabled={submitting}
              >
                Девочка
              </button>
            </div>
            {errors.gender && (
              <div className="rb-field__hint">{errors.gender}</div>
            )}
          </div>

          <Field
            id="signup-email"
            label="Email родителя"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="you@example.com"
            error={errors.email}
            autoComplete="email"
            disabled={submitting}
            inputMode="email"
          />

          <Field
            id="signup-password"
            label="Пароль"
            type={showPw ? "text" : "password"}
            value={password}
            onChange={setPassword}
            placeholder="Минимум 6 символов"
            error={errors.password}
            autoComplete="new-password"
            disabled={submitting}
          />
          <button
            type="button"
            className="rb-field__toggle-pw"
            onClick={() => setShowPw((v) => !v)}
          >
            {showPw ? "Скрыть пароль" : "Показать пароль"}
          </button>

          {/* Тройная CAPTCHA — обязательна для всех регистраций (2026-05-28).
              Реальный юзер решает за 5с, OCR-боты не пройдут две капчи + рисунок. */}
          {/* В identity v2 капчи нет: сервис её не принимает и не проверяет.
              Показывать три задания, результат которых никуда не уйдёт, —
              значит заставлять ребёнка делать бессмысленную работу. */}
          {CAPABILITIES.captcha && (
            <TripleCaptcha ref={captchaRef} disabled={submitting} />
          )}

          {/* Согласия — 2 раздельных чекбокса (152-ФЗ ст. 9) */}
          <div className="rb-consent">
            <label className="rb-consent__item">
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                disabled={submitting}
              />
              <span>
                Я являюсь законным представителем (родителем или опекуном)
                несовершеннолетнего, мне исполнилось 18 лет. Принимаю{" "}
                <Link to="/terms" target="_blank">
                  Пользовательское соглашение
                </Link>
                .
              </span>
            </label>
            <label className="rb-consent__item">
              <input
                type="checkbox"
                checked={agreePdn}
                onChange={(e) => setAgreePdn(e.target.checked)}
                disabled={submitting}
              />
              <span>
                Даю согласие на обработку моих персональных данных и
                персональных данных ребёнка в соответствии с{" "}
                <Link to="/privacy-policy" target="_blank">
                  Политикой конфиденциальности
                </Link>{" "}
                на основании ст. 9 ФЗ-152.
              </span>
            </label>
          </div>

          {/* loading вместо подмены текста: «Создаём аккаунт…» шире исходной
              надписи, кнопка прыгала под курсором. */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={submitting}
            disabled={!agreeTerms || !agreePdn}
            className="rb-auth__submit"
          >
            Создать аккаунт
          </Button>

          {/* См. комментарий в LoginPage: при v2 провайдеры недоступны. */}
          {/* Кнопки Google нет: решение Мина 25.09.2026 — убрана со всех
              сервисов. Раньше здесь стояла заглушка-объяснение «вход через
              Google недоступен». */}
          <OAuthDivider />
          {CAPABILITIES.yandexOAuth && <YandexLoginButton disabled={submitting} />}

          <div className="rb-auth__switch">
            Уже есть аккаунт? <Link to="/login">Войти</Link>
          </div>
        </form>
      </div>
    </Layout>
  );
}
